// externals
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { Action } from '@interstate/components/Action';
import { Alert } from '@interstate/components/Alert';
import { Grid } from '@interstate/components/Grid';
import { Typography } from '@interstate/components/Typography';
import * as creditDecisionSelectors from '@makemydeal/dr-dash-store/selectors/creditDecisionSelectors';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as navigationActionCreators from '@makemydeal/dr-dash-store/actions/navigationActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as creditDecisionActionCreators from '@makemydeal/dr-dash-store/actions/creditDecisionActionCreators';

import type { StateTree } from '@makemydeal/dr-dash-types';
import { LEASE } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';

// components
import AcquisitionFeeAmountField from './common/AcquisitionFeeAmountField.interstate';
import AdjustedResidualField from './common/AdjustedResidualField.interstate';
import BaseResidualField from './common/BaseResidualField.interstate';
import BuyRateField from './common/BuyRateField.interstate';
import CashDownField from './common/CashDownField.interstate';
import CreditDecisionLenderNameField from './common/CreditDecisionLenderNameField.interstate';
import CreditDecisionStatusField from './common/CreditDecisionStatusField.interstate';
import CustomMileageField from './common/CustomMileageField.interstate';
import CustomMileageFieldNumeric from './common/CustomMileageFieldNumeric.interstate';
import ExcessMileageChargeField from './common/ExcessMileageChargeField.interstate';
import LeaseTermField from './common/LeaseTermField.interstate';
import MaxResidualizedMsrpField from './common/MaxResidualizedMsrpField.interstate';
import MilesPerYearField from './common/MilesPerYearField.interstate';
import PaymentFrequencyField from './common/PaymentFrequencyField.interstate';
import RetailPriceField from './common/RetailPriceField.interstate';
import SecurityDepositField from './common/SecurityDepositField.interstate';
import SellRateField from './common/SellRateField.interstate';
import { TermsTab } from './common/Terms.style';
import AlertError from './gridTable/components/AlertError';
import { useIsAspPaymentGridRedesignActive } from './gridTable/hooks/useIsAspPaymentGridRedesignActive';
import { LockWarning } from '../dealLocking';

// styles
import { ContainerGrid } from '../common/ContainerGrid.style';
import { ContainerGridItem } from '../common/ContainerGridItem';
import { LeaseHeader } from './Lease.styles';
import PaymentGridToggle from './common/PaymentGridToggle';

// constants/utils
import type { IPaymentGridErrorState } from '@makemydeal/dr-dash-types';
import { useOktaAuth } from '@okta/okta-react';
import { useOrderAgnosticILDPURL } from '../hooks';
import { GENERIC_LAST_FIELD_CHANGED_ERROR, LEASE_PAYMENT_GRID_PROGRAM_DISCLAIMER } from '../constants';
import SellingPriceFieldWithDealerDiscounts from './common/SellingPriceField.interstate';
import { setAuthTokenCookie } from '@makemydeal/dr-shared-ui-utils';

export type LeaseProps = {
    enableCreditDecisionAlert?: boolean;
    isInsideSlideOut?: boolean;
};

const Lease = ({ enableCreditDecisionAlert = false, isInsideSlideOut = false }: LeaseProps) => {
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const lenderDecisions = useSelector((state: StateTree) => {
        return creditDecisionSelectors.getLenderCreditDecisionsByOfferType(state, currentOfferType);
    });

    const enableCreditBureauAndDecisionPBCRouteOne = useSelector(featureToggleSelectors.enableCreditBureauAndDecisionPBCRouteOne);
    const enableOrderAgnosticILDPUI = useSelector(featureToggleSelectors.enableOrderAgnosticILDPUI);

    const isCreditProviderRouteOne: boolean = useSelector(dealerSelectors.isCreditProviderRouteOne);
    const dispatch = useDispatch();

    const creditDecisionAvailable = lenderDecisions.length > 0;

    const navigateToCreditBureau = () => dispatch(navigationActionCreators.navigateToCreditBureau());
    const navigateToCreditDecision = () => dispatch(navigationActionCreators.navigateToCreditDecision());
    const openCreditDecisionSlideOut = () => dispatch(creditDecisionActionCreators.openCreditDecisionSlideOut());
    const shouldNavigateToCreditDecision =
        !isCreditProviderRouteOne || (enableCreditBureauAndDecisionPBCRouteOne && isCreditProviderRouteOne);

    const paymentGridEnabled: boolean = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, currentOfferType)
    );

    const showAllLeaseFields = !paymentGridEnabled;
    const paymentGridErrorState: IPaymentGridErrorState = useSelector(offerReduxSelectors.getPaymentGridErrorState);

    const isUserProgramQuotes = useSelector(offerReduxSelectors.getIsUserProgramQuotes);

    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);
    const orderDetailsLink = useSelector(dealXgDetailsSelectors.getOrderDetailsLink);
    const ildpDetailsLink = useSelector(dealXgDetailsSelectors.getIldpDetailsLink);
    const isEnableCustomLeaseMileageByDealer = useSelector(featureToggleSelectors.getEnableCustomLeaseMileageByDealer);
    const isEnableCustomLeaseMileageByFeature = useSelector(featureToggleSelectors.getEnableCustomLeaseMileageByFeature);
    const isMileageOptionsUIUpdateEnabled = useSelector(featureToggleSelectors.isMileageOptionsUIUpdateEnabled);
    const isCreditDecisionEnabled = useSelector(offerReduxSelectors.getCreditDecisionEnabled);
    const oktaContext = useOktaAuth();
    const getJwtToken = () => oktaContext?.oktaAuth.getAccessToken() || '';
    const orderAgnosticILDPURL = useOrderAgnosticILDPURL();
    const paymentGridEnabledToggle = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, currentOfferType)
    );

    /**
     * The grid disclaimer is suppressed under the redesign: it describes the pre-redesign behaviour, where opening a
     * Lease grid forced the deal onto program-quotes. Gated on the same gate as the behaviour it warns about - a raw
     * toggle read misses Flex Buy, where the deal *is* still forced and the warning still applies.
     */
    const isAspPaymentGridRedesignActive = useIsAspPaymentGridRedesignActive();

    const showLeasePaymentGridDisclaimer = paymentGridEnabledToggle && !isCreditDecisionEnabled && !isAspPaymentGridRedesignActive;

    const showsGridErrorBanner = !useSelector(offerReduxSelectors.hasPaymentGridColumnMessage);

    /*
    Two feaureToggles:
        1. isEnableCustomLeaseMileageByDealer to control by Dealer
        2. isEnableCustomLeaseMileageByFeature to control the enviroment level
    */
    const showCustomLileageField = isEnableCustomLeaseMileageByDealer && isEnableCustomLeaseMileageByFeature;

    const handleViewLenderDecisionClick = () => {
        if (isEcommerceDeal && currentOfferType === LEASE) {
            const token = getJwtToken();
            const orderLink = ildpDetailsLink || `${orderDetailsLink}?show=ildp`;
            if (ildpDetailsLink) {
                setAuthTokenCookie(token);
            }
            window.open(`${orderLink}`, '_blank');
        } else if (!isEcommerceDeal && enableOrderAgnosticILDPUI && orderAgnosticILDPURL) {
            const token = getJwtToken();
            setAuthTokenCookie(token);
            window.open(`${orderAgnosticILDPURL}`, '_blank');
        } else if (isInsideSlideOut && shouldNavigateToCreditDecision) {
            openCreditDecisionSlideOut();
        } else if (shouldNavigateToCreditDecision) {
            navigateToCreditDecision();
        } else {
            navigateToCreditBureau();
        }
    };

    if (currentOfferType !== LEASE) return null;

    const leaseFields = [
        { component: <CashDownField />, key: 'CashDownField' },
        { component: <CreditDecisionLenderNameField />, key: 'CreditDecisionLenderNameField' },
        { component: <CreditDecisionStatusField />, key: 'CreditDecisionStatusField' },
        { component: <LeaseTermField />, key: 'LeaseTermField' },
        { component: <PaymentFrequencyField />, key: 'PaymentFrequencyField' },
        { component: <SellRateField />, key: 'SellRateField' },
        { component: <BuyRateField />, key: 'BuyRateField' },
        { component: <AcquisitionFeeAmountField />, key: 'AcquisitionFeeAmountField' },
        { component: <BaseResidualField />, key: 'BaseResidualField' },
        { component: <AdjustedResidualField />, key: 'AdjustedResidualField' },
        showCustomLileageField
            ? {
                  component: isMileageOptionsUIUpdateEnabled ? <CustomMileageFieldNumeric /> : <CustomMileageField />,
                  key: 'CustomMileageField'
              }
            : {
                  component: <MilesPerYearField />,
                  key: 'MilesPerYearField'
              },
        { component: <MaxResidualizedMsrpField />, key: 'MaxResidualizedMsrpField' },
        { component: <SecurityDepositField />, key: 'SecurityDepositField' },
        { component: <ExcessMileageChargeField />, key: 'ExcessMileageChargeField' }
    ];

    return (
        <TermsTab data-testid="lease-terms">
            <LeaseHeader>
                <Typography data-testid="lease-title" variant="h5">
                    Payment And Lender Information
                </Typography>
                <Action size="sm" data-testid="lease-terms-edit-link" onClick={handleViewLenderDecisionClick}>
                    View Lender Decisions
                </Action>
            </LeaseHeader>
            <Grid xs={12}>
                {enableCreditDecisionAlert && creditDecisionAvailable && (
                    <Alert id="infoAlert" role="infoAlert" type="info" data-testid="terms-lender-decision-alert">
                        Lender decisions are ready for this offer.
                    </Alert>
                )}
            </Grid>
            <>
                {showLeasePaymentGridDisclaimer && (
                    <Grid xs={12} padding="0" marginBottom="1rem">
                        <Alert role="banner" type="info" data-testid="program-alert-warning">
                            <Typography variant="label-md">{LEASE_PAYMENT_GRID_PROGRAM_DISCLAIMER}</Typography>
                        </Alert>
                    </Grid>
                )}
                {isUserProgramQuotes && !isCreditDecisionEnabled && (
                    <Grid xs={12} padding="0" marginBottom="1rem">
                        <Alert role="banner" type="info" data-testid="upq-alert-warning-lease">
                            <Typography variant="label-md">
                                To be able to use Payment Grid, go to Programs and Incentives to select.
                            </Typography>
                        </Alert>
                    </Grid>
                )}
                {/* Under the Lease redesign this is the FALLBACK voice, not the primary one. `hasError` is grid-WIDE -
                    `PAYMENT_FAILURE` sets it for any row carrying `meta.paymentGrid.gridAction`, main or not - so one
                    column's failure claimed the whole grid had failed, at page level, over columns that had priced
                    perfectly well. Each column now carries its own message on its Sell Rate and Residual: `Required Info.`
                    on a blank one, the failure copy on a filled one once a complete pair was asked for and rejected (AC-LSE-8).

                    But the per-column states cannot cover every failure the banner used to. A column priced on
                    program-quotes holds neither half of a user pair, so the rejected state cannot fire for it, and the
                    awaiting state is gated on the user having acted on that column - which they did not if they edited a
                    cash-down row. A real failure there would be silent everywhere. So: suppressed whenever a column can
                    speak for itself, shown when nothing can. Finance keeps the banner unconditionally. */}
                {paymentGridErrorState.hasError && paymentGridEnabled && showsGridErrorBanner && (
                    <AlertError message={GENERIC_LAST_FIELD_CHANGED_ERROR} />
                )}
                <LockWarning />
                <Grid xs={12} paddingLeft="0">
                    <PaymentGridToggle currentOfferType={currentOfferType} />
                </Grid>
            </>

            <ContainerGrid data-testid="main-lease-content">
                <ContainerGridItem xs={6} md={4} alignSelf="baseline">
                    <RetailPriceField />
                </ContainerGridItem>
                <ContainerGridItem xs={6} md={4} alignSelf="baseline">
                    <SellingPriceFieldWithDealerDiscounts />
                </ContainerGridItem>
                {showAllLeaseFields && (
                    <>
                        {leaseFields.map(({ key, component }) => (
                            <ContainerGridItem alignSelf="baseline" key={key} xs={6} md={4}>
                                {component}
                            </ContainerGridItem>
                        ))}
                    </>
                )}
                {paymentGridEnabled && (
                    <>
                        <ContainerGridItem xs={6} md={4} alignSelf="baseline">
                            <MilesPerYearField />
                        </ContainerGridItem>
                        <ContainerGridItem xs={6} md={4} alignSelf="baseline">
                            <SecurityDepositField />
                        </ContainerGridItem>
                        <ContainerGridItem xs={6} md={4} alignSelf="baseline">
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
                                <AcquisitionFeeAmountField />
                            </div>
                        </ContainerGridItem>
                        <ContainerGridItem xs={6} md={4} alignSelf="baseline">
                            &nbsp;
                        </ContainerGridItem>
                    </>
                )}
            </ContainerGrid>
        </TermsTab>
    );
};

export default Lease;
